import { DataGrid, NumberBox } from "devextreme-react";
import { useRef } from "react";
import { handleCellStyle } from "../utils";
import {
  Column,
  Editing,
  FilterRow,
  HeaderFilter,
  Item,
  LoadPanel,
  RequiredRule,
  Scrolling,
  Toolbar,
} from "devextreme-react/data-grid";
import HtmlEditor, {
  Toolbar as HtmlEditorToolbar,
  Item as HtmlEditorItem,
  MediaResizing,
  ImageUpload,
} from "devextreme-react/html-editor";

import styled from "styled-components";
import { fontValues, sizeValues } from "../data";

const EnrollStatusEditorWrapper = styled.div`
  margin: 0 20px;
  > div:not(:first-child) {
    margin-top: 24px;
  }
  > div:nth-child(2) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    text-align: center;
    > div {
      padding: 8px 16px;
      font-weight: bold;
      font-size: 1rem;
    }
    .dx-texteditor-input {
      text-align: center;
    }
  }
  > div:last-child {
    > div:first-child {
      font-size: 1rem;
      font-weight: bold;
      margin-bottom: 4px;
    }
  }
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-toolbar .dx-toolbar-label {
    font-weight: bold;
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
  .ql-editor.dx-htmleditor-content {
    img {
      max-width: 800px;
    }
  }
`;

const EnrollStatusEditor = ({
  nSubject, //* 당월 등록 시험대상자 수.
  nSubjectRef, //* 당월 등록 시험대상자 NumberBox의 ref.
  prevMonthStackedSubject, //* 전월까지의 누적 시험대상자 수.
  subjectMemo, //* yearMonth의 시험등록자에 대한 설명
  subjectMemoRef, //* yearMonth의 시험등록자에 대한 설명을 적는 HtmlEditor의 ref.
  targetSubjectDataSource, //* "시기별 목표 시험대상자 수" grid에 들어가는 dataSource.
  targetSubjectGridRef, //* "시기별 목표 시험대상자 수" DataGrid의 ref.
  yearMonth, //* 연도와 월 정보가 담긴 문자열.
}) => {
  const [year, month] = yearMonth.split("-");
  const stackedSubjectRef = useRef(null);

  const handleChangeValue = (e) => {
    stackedSubjectRef.current.instance.option(
      "value",
      prevMonthStackedSubject + e.value
    );
  };

  const handleInitNewRow = (e) => {
    e.data.yearMonth = null;
    e.data.nSubjects = null;
  };

  return (
    <EnrollStatusEditorWrapper>
      <div>
        <DataGrid
          allowColumnResizing={true}
          columnResizingMode={"nextColumn"}
          dataSource={targetSubjectDataSource}
          height={400}
          id="monthlyEnrollStatusMemo"
          onCellPrepared={handleCellStyle}
          onInitNewRow={handleInitNewRow}
          ref={targetSubjectGridRef}
          showBorders={true}
          showColumnLines={true}
          width={"100%"}
          wordWrapEnabled={true}
        >
          <Editing
            allowAdding={true}
            allowDeleting={true}
            allowUpdating={true}
            confirmDelete={false}
            mode="cell"
          />
          {/* <FilterRow visible={true} /> */}
          <HeaderFilter visible={true} />
          <LoadPanel enabled={true} />
          <Scrolling mode="virtual" showScrollbar={"always"} />
          <Toolbar>
            <Item
              location="before"
              name="title"
              text="Target Number of Subjects Enrolled by Period"
            />
            <Item location="after" name="addRowButton" />
          </Toolbar>
          <Column
            dataField="yearMonth"
            alignment="center"
            caption="Year-Month"
            dataType="date"
            editorOptions={{
              calendarOptions: {
                maxZoomLevel: "year",
                minZoomLevel: "decade",
                zoomLevel: "year",
              },
              dateSerializationFormat: "yyyy-MM",
              pickerType: "calendar",
              useMaskBehavior: true,
            }}
            format="yyyy-MM"
            sortOrder="desc"
          >
            <RequiredRule message="" />
          </Column>
          <Column
            dataField="nSubjects"
            alignment="center"
            caption="Target Number of Subjects Enrolled by Period"
            dataType="number"
            editorOptions={{ format: "#례" }}
            showEditorAlways={true}
          >
            <RequiredRule message="" />
          </Column>
        </DataGrid>
      </div>
      <div>
        <div>
          Number of Subjects Enrolled This Month ({year}-{month})
        </div>
        <div>Total Number of Enrolled Subjects</div>
        <NumberBox
          id="currentValue"
          defaultValue={nSubject}
          format="0,###례"
          min={0}
          onValueChanged={handleChangeValue}
          ref={nSubjectRef}
        />
        <NumberBox
          id="stackedValue"
          defaultValue={prevMonthStackedSubject + nSubject}
          format="0,###례"
          min={0}
          readOnly={true}
          ref={stackedSubjectRef}
        />
      </div>
      <div>
        <div>Description of Subjects Enrolled This Month</div>
        <HtmlEditor
          defaultValue={subjectMemo}
          height={350}
          id="newlyRegisteredMemoEditor"
          ref={subjectMemoRef}
          width={"100%"}
        >
          <HtmlEditorToolbar multiline={true}>
            <HtmlEditorItem name="undo" />
            <HtmlEditorItem name="redo" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="size" acceptedValues={sizeValues} />
            <HtmlEditorItem name="font" acceptedValues={fontValues} />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="bold" />
            <HtmlEditorItem name="italic" />
            <HtmlEditorItem name="strike" />
            <HtmlEditorItem name="underline" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="alignLeft" />
            <HtmlEditorItem name="alignCenter" />
            <HtmlEditorItem name="alignRight" />
            <HtmlEditorItem name="alignJustify" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="orderedList" />
            <HtmlEditorItem name="bulletList" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="color" />
            <HtmlEditorItem name="background" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="link" />
            <HtmlEditorItem name="clear" />
            <HtmlEditorItem name="image" />
            <HtmlEditorItem name="separator" />
            <HtmlEditorItem name="insertTable" />
            <HtmlEditorItem name="deleteTable" />
            <HtmlEditorItem name="insertRowAbove" />
            <HtmlEditorItem name="insertRowBelow" />
            <HtmlEditorItem name="deleteRow" />
            <HtmlEditorItem name="insertColumnLeft" />
            <HtmlEditorItem name="insertColumnRight" />
            <HtmlEditorItem name="deleteColumn" />
          </HtmlEditorToolbar>
          <ImageUpload tabs={["url", "file"]} fileUploadMode={"base64"} />
          <MediaResizing enabled={true} />
        </HtmlEditor>
      </div>
    </EnrollStatusEditorWrapper>
  );
};

export default EnrollStatusEditor;
